import React, { useEffect, useState } from 'react';

import { NativeResources } from '@ariksa/inventory-core/api';
import { Box, Stack, Text } from '@chakra-ui/react';
import { map, sum, values } from 'lodash';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { customTheme } from 'theme';

import formatNumber from 'components/DataDisplay/Utils/formatNumber';
import { PrimaryButton } from 'components/DataEntry';
import { getIconColor } from 'components/Icons/Components/getIconColor';
import { CustomTooltip } from 'components/Visualization/Charts/CustomTooltip';
import { useResourceType } from 'containers/App/hooks/useResourceType';
import { selectDataSecurityOverview } from 'containers/Dashboard/DataSecurityOverview/selectors';
import { renderNoEntitiesFoundWithIcon } from 'containers/Dashboard/utils';
import { DataSourcesCardWithBarChart } from 'containers/Dashboard/utils/DataSourcesCardWithBarChart';

export const DataSourcesAndSensitiveData: React.FC = () => {
  const { sensitiveDataBySources } = useSelector(selectDataSecurityOverview);
  const { getResourceAlias } = useResourceType();
  const [chartData, setChartData] = useState<Record<string, any>[]>([]);
  const navigate = useNavigate();
  const [totalSources, setTotalSources] = useState(0);

  useEffect(() => {
    const sensitiveData = sensitiveDataBySources.data;
    // Each row is only built when its value is truthy, so inside a row the value
    // is read directly; the old `?.` / `?? 0` fallbacks there could never run.
    setChartData([
      ...(!sensitiveData?.Buckets
        ? []
        : [
            {
              name: 'Buckets',
              color: customTheme.colors.primary,
              'Sensitive data count': sensitiveData.Buckets,
              count: formatNumber(sensitiveData.Buckets),
              total: sensitiveData.Buckets,
            },
          ]),
      ...(!sensitiveData?.DB
        ? []
        : [
            {
              name: 'Databases',
              color: getIconColor(NativeResources.RelationalDatabaseService),
              'Sensitive data count': sensitiveData.DB,
              count: formatNumber(sensitiveData.DB),
              total: sensitiveData.DB,
            },
          ]),
      ...(!sensitiveData?.Others
        ? []
        : [
            {
              name: 'Disks',
              color: customTheme.colors.skyBlue['300'],
              'Sensitive data count': sensitiveData.Others,
              count: formatNumber(sensitiveData.Others),
              total: sensitiveData.Others,
            },
          ]),
      ...(!sensitiveData?.SaaS
        ? []
        : [
            {
              name: 'SaaS',
              color: customTheme.colors.orange['400'],
              'Sensitive data count': sensitiveData.SaaS,
              count: formatNumber(sensitiveData.SaaS),
              total: sensitiveData.SaaS,
            },
          ]),
    ]);
  }, [sensitiveDataBySources.data, getResourceAlias]);

  useEffect(() => {
    setTotalSources(sum(map(chartData, 'total')));
  }, [chartData]);

  const customTooltipContent = props => {
    const { active, payload } = props;
    if (active && payload && payload.length) {
      // payload.length is checked above, so payload[0] needs no `?? {}` fallback.
      const { payload: obj } = payload[0];

      return (
        <>
          <CustomTooltip>
            <Stack fontSize={'sm'}>
              <Text as="b">{obj.name}</Text>
              <Text>
                Sensitive data count:{' '}
                <Text as="b">{formatNumber(obj['Sensitive data count'])}</Text>
              </Text>
            </Stack>
          </CustomTooltip>
        </>
      );
    }

    return null;
  };

  return (
    <DataSourcesCardWithBarChart
      label="Sensitive Data by Source Types"
      noDataMessage={renderNoEntitiesFoundWithIcon('No sensitive data sources')}
      /*noDataMessage={
        <Stack w="35%" spacing={4}>
          <Box color="primary">Deploy Outpost for Data for this insight</Box>
          <Box>
            <PrimaryButton
              size="xs"
              fontSize="sm"
              px={3}
              onClick={() => navigate('/setup/outpost/add/data')}
            >
              DEPLOY
            </PrimaryButton>
          </Box>
        </Stack>
      }*/
      chartData={chartData}
      xField="name"
      yFields={['Sensitive data count']}
      barChartColors={map(chartData, o => o.color)}
      isLoadingChart={sensitiveDataBySources.isLoading}
      labelListKey="Sensitive data count"
      onClick={(e, k, v) => {
        navigate('/visibility/data?show=Details&tab=DataSources');
      }}
      subHeader={
        chartData?.length +
        ' type' +
        (chartData?.length === 1 ? ', ' : 's, ') +
        formatNumber(totalSources, 1) +
        ' sensitive data count' /*+
        (totalSources === 1 ? '' : 's')*/
      }
      customTooltipContent={customTooltipContent}
      isLoadingSensitiveData={sensitiveDataBySources.isLoading}
      totalSensitiveData={sum(values(sensitiveDataBySources.data))}
      tooltip={{
        tooltip: (
          <Stack>
            <Box>
              Ariksa Outpost for Data discovers and classifies sensitive data in
              your environment continuously.
            </Box>
            <Box>
              Deploy Outpost for Data in the region of your data sources and add
              data sources to scan
            </Box>
          </Stack>
        ),
        header: 'Discover your sensitive data',
        footerText: 'Take me there',
        onClickFooter: () => navigate('/setup/outpost/add/data'),
      }}
      iconBgColor={getIconColor(NativeResources.ElasticBlockStorage)}
    />
  );
};
